/*
 * Copyright (c) 2022 biocrates life sciences ag, All rights reserved.
 */

interface Mime {
    mime: string
    pattern: number[]
    mask: number[]
}

/**
 * Load the mime type based on the signature of the first bytes of the file
 * Adapted from https://stackoverflow.com/a/42983450
 */
function readMimeFromFileSignature(
    file: File,
    callback: (mime: string | undefined) => void
) {
    const mimes: Mime[] = [
        {
            mime: "image/jpeg",
            pattern: [0xff, 0xd8, 0xff],
            mask: [0xff, 0xff, 0xff],
        },
        {
            mime: "image/png",
            pattern: [0x89, 0x50, 0x4e, 0x47],
            mask: [0xff, 0xff, 0xff, 0xff],
        },
        {
            mime: "image/bmp",
            pattern: [0x42, 0x4d],
            mask: [0xff, 0xff],
        },
        {
            mime: "image/gif",
            pattern: [0x47, 0x49, 0x46],
            mask: [0xff, 0xff, 0xff],
        },
        {
            mime: "application/pdf",
            pattern: [0x25, 0x50, 0x44, 0x46, 0x2d],
            mask: [0xff, 0xff, 0xff, 0xff, 0xff],
        },
        // Other signatures can be found at https://mimesniff.spec.whatwg.org/#matching-an-image-type-pattern
        // or https://github.com/sindresorhus/file-type/blob/main/core.js
    ]

    function check(bytes: Uint8Array, mime: Mime) {
        for (let i = 0, l = mime.mask.length; i < l; i += 1) {
            // eslint-disable-next-line no-bitwise
            if ((bytes[i] & mime.mask[i]) - mime.pattern[i] !== 0) {
                return false
            }
        }
        return true
    }

    const blob = file.slice(0, 5) // read the first 5 bytes of the file as pdf has a mask of 5 bytes

    const reader = new FileReader()
    // eslint-disable-next-line consistent-return
    reader.onloadend = function (e) {
        if (e.target?.readyState === FileReader.DONE) {
            const bytes = new Uint8Array(e.target.result as ArrayBuffer)

            for (let i = 0, l = mimes.length; i < l; i += 1) {
                if (check(bytes, mimes[i])) return callback(mimes[i].mime)
            }

            return callback(undefined)
        }
    }
    reader.readAsArrayBuffer(blob)
}

/**
 * Prepares a function to handle the dropped files. Accepts all mimetypes
 * given in accepts. accept gets invoked for each accepted file and reject
 * gets invoked for rejected file.
 */
const getHandleDroppedItems = (
    accepts: string[],
    accept: (acceptedFile: File) => void,
    reject: (rejectedFile: File) => void
) => {
    const handleDroppedItems = (items: DataTransferItemList) => {
        for (let i = 0; i < items.length; i += 1) {
            const entry = items[i].webkitGetAsEntry()
            if (entry && entry.isFile) {
                entry.file((file: File) => {
                    readMimeFromFileSignature(file, (mime) => {
                        if (mime && accepts.includes(mime)) {
                            accept(file)
                        } else {
                            reject(file)
                        }
                    })
                })
            }
        }
    }
    return handleDroppedItems
}

export default getHandleDroppedItems
